import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import {
  ShoppingCart,
  User,
  X,
  Search,
  LogOut,
  Settings,
  Sun,
  Moon,
  Monitor,
  ClipboardList
} from 'lucide-react';
import TEXT_CONSTANTS from '../constants/text';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, isAuthenticated, logout } = useAuth();
  const { itemCount } = useCart();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  
  const desktopUserMenuRef = useRef<HTMLDivElement>(null);
  const mobileUserMenuRef = useRef<HTMLDivElement>(null);
  const themeMenuRef = useRef<HTMLDivElement>(null);
  const searchMenuRef = useRef<HTMLDivElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);

  // Handle clicks outside menus to close them
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Add a small delay to allow navigation to complete first
      setTimeout(() => {
        if (desktopUserMenuRef.current && !desktopUserMenuRef.current.contains(event.target as Node) &&
            mobileUserMenuRef.current && !mobileUserMenuRef.current.contains(event.target as Node)) {
          setIsUserMenuOpen(false);
        }
        if (themeMenuRef.current && !themeMenuRef.current.contains(event.target as Node)) {
          setIsThemeMenuOpen(false);
        }
        if (searchMenuRef.current && !searchMenuRef.current.contains(event.target as Node) &&
            searchButtonRef.current && !searchButtonRef.current.contains(event.target as Node)) {
          setIsMenuOpen(false);
        }
      }, 150);
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
    navigate('/');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsMenuOpen(false);
    }
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className="h-5 w-5" />;
      case 'dark':
        return <Moon className="h-5 w-5" />;
      default:
        return <Monitor className="h-5 w-5" />;
    }
  };

  return (
    <nav className="bg-white dark:bg-secondary-900 shadow-md fixed top-0 left-0 right-0 z-50 transition-colors w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 w-full min-w-0">
          {/* Logo and main nav */}
          <div className="flex items-center min-w-0 flex-1">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img 
                src="/logo.png" 
                alt={TEXT_CONSTANTS.BRAND.WEBSITE_NAME_SHORT}
                className="h-12 w-auto max-w-40 sm:h-12 md:h-14"
              />
              <div className="ml-2 sm:ml-3">
                <span className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 tracking-tight">
                  {TEXT_CONSTANTS.BRAND.WEBSITE_NAME_SHORT}
                </span>
                <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                  {TEXT_CONSTANTS.BRAND.TAGLINE}
                </div>
              </div>
            </Link>

            <div className="hidden lg:ml-8 lg:flex lg:space-x-8">
              <Link
                to="/"
                className="text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-2 text-sm font-medium transition-colors"
              >
                {TEXT_CONSTANTS.NAV.HOME}
              </Link>
              <Link
                to="/products"
                className="text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-2 text-sm font-medium transition-colors"
              >
                {TEXT_CONSTANTS.NAV.PRODUCTS}
              </Link>
            </div>
          </div>

          {/* Desktop search and actions */}
          <div className="hidden md:flex items-center space-x-2 lg:space-x-4">
            {/* Search bar - hidden on smaller screens, visible on lg+ */}
            <form onSubmit={handleSearch} className="hidden lg:block">
              <div className="relative">
                <input
                  type="text"
                  placeholder={TEXT_CONSTANTS.PRODUCTS.SEARCH_PRODUCTS}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-48 xl:w-64 pl-10 pr-4 py-2 border border-gray-300 dark:border-secondary-600 
                           bg-white dark:bg-secondary-800 text-gray-900 dark:text-gray-100
                           rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent
                           placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400 dark:text-gray-500" />
              </div>
            </form>

            {/* Theme toggle - Desktop only for admin focus */}
            <div className="relative hidden lg:block" ref={themeMenuRef}>
              <button
                onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
                className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 
                         p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-800 transition-colors"
                title="Change theme"
              >
                {getThemeIcon()}
              </button>

              {isThemeMenuOpen && (
                <div className="absolute right-0 mt-2 w-36 bg-white dark:bg-secondary-800 rounded-md shadow-lg py-1 z-[60] 
                               border border-gray-200 dark:border-secondary-700">
                  <button
                    onClick={() => {
                      setTheme('light');
                      setIsThemeMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-secondary-700 
                               transition-colors ${theme === 'light' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    <Sun className="h-4 w-4 mr-3" />
                    Light
                  </button>
                  <button
                    onClick={() => {
                      setTheme('dark');
                      setIsThemeMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-secondary-700 
                               transition-colors ${theme === 'dark' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    <Moon className="h-4 w-4 mr-3" />
                    Dark
                  </button>
                  <button
                    onClick={() => {
                      setTheme('system');
                      setIsThemeMenuOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-secondary-700 
                               transition-colors ${theme === 'system' ? 'text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-gray-300'}`}
                  >
                    <Monitor className="h-4 w-4 mr-3" />
                    System
                  </button>
                </div>
              )}
            </div>

            {/* Cart */}
            <Link
              to="/cart"
              className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 
                       p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-800 relative transition-colors"
              title="Shopping Cart"
            >
              <ShoppingCart className="h-6 w-6" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary-600 dark:bg-primary-500 text-white text-xs 
                               rounded-full h-5 w-5 flex items-center justify-center font-medium">
                  {itemCount > 99 ? '99+' : itemCount}
                </span>
              )}
            </Link>

            {/* User menu */}
            {isAuthenticated ? (
              <div className="relative" ref={desktopUserMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-primary-600 
                           dark:hover:text-primary-400 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-800 
                           transition-colors"
                >
                  <User className="h-6 w-6" />
                  <span className="hidden xl:block text-sm font-medium max-w-24 truncate">
                    {user?.name}
                  </span>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-secondary-800 rounded-md shadow-lg py-1 z-[60] 
                                 border border-gray-200 dark:border-secondary-700">
                    {user?.isAdmin && (
                      <Link
                        to="/admin/dashboard"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 
                                 hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings className="h-4 w-4 mr-3" />
                        Admin Dashboard
                      </Link>
                    )}
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 
                               hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      Profile
                    </Link>
                    <Link
                      to="/orders"
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 
                               hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      My Orders
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 
                               hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex space-x-2">
                <Link
                  to="/login"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 
                           px-3 py-2 text-sm font-medium transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                           text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Register
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2 flex-shrink-0">
            {/* Mobile search button */}
            <button
              ref={searchButtonRef}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 
                       p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-800 transition-colors"
              title="Search products"
            >
              <Search className="h-5 w-5" />
            </button>

            {/* Mobile user menu */}
            {isAuthenticated ? (
              <div className="relative" ref={mobileUserMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center text-gray-600 dark:text-gray-300 hover:text-primary-600 
                           dark:hover:text-primary-400 p-1.5 rounded-lg transition-colors"
                  title="Account menu"
                >
                  <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    <span className="text-base font-medium text-primary-600 dark:text-primary-400">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-secondary-800 rounded-lg shadow-lg py-1 z-[60] 
                                 border border-gray-200 dark:border-secondary-700">
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-secondary-700">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{user?.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</div>
                    </div>
                    
                    {user?.isAdmin && (
                      <Link
                        to="/admin/dashboard"
                        className="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 
                                 hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <Settings className="h-4 w-4 mr-3" />
                        Admin Panel
                      </Link>
                    )}
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 
                               hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User className="h-4 w-4 mr-3" />
                      My Profile
                    </Link>
                    <Link
                      to="/orders"
                      className="flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 
                               hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <ClipboardList className="h-4 w-4 mr-3" />
                      My Orders
                    </Link>
                    <div className="border-t border-gray-200 dark:border-secondary-700 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 
                               hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 
                           px-3 py-2 text-sm font-medium transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-secondary-800"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 
                           text-white px-3 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile search overlay */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-gray-200 dark:border-secondary-700 bg-white dark:bg-secondary-900" ref={searchMenuRef}>
          <div className="px-4 py-3">
            <form onSubmit={handleSearch}>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-secondary-600 
                           bg-white dark:bg-secondary-800 text-gray-900 dark:text-gray-100
                           rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent
                           placeholder-gray-500 dark:placeholder-gray-400 transition-colors text-base"
                  autoFocus
                />
                <Search className="absolute left-3 top-3.5 h-6 w-6 text-gray-400 dark:text-gray-500" />
                <button
                  type="button"
                  onClick={() => setIsMenuOpen(false)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Press Enter to search products
              </div>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;